import { Form, FormElement, FormError, FormGroup, FormSubmission, HandlerType, NextBehavior, ReducerType } from '@uplift/types';
import React, { useContext } from 'react';

export type RenderFormContextData = {
  element: FormElement;
  form: Form;
  groups: FormGroup[];
  loading: boolean;
  totalElements: number;
  submissionId: string;
  nextBehavior: NextBehavior;
  error?: FormError;
  elements?: FormElement[];
  submission?: FormSubmission;
  reviewPageElement?: FormElement;
  shouldRefetch?: boolean;
  answerVal?: string | string[];
};

export type RenderFormContextState = {
  state: RenderFormContextData;
  dispatch: React.Dispatch<Action>;
};

type InitAction = {
  type: 'init';
  payload: {
    form: Form;
    element: FormElement;
    totalElements: number;
    submissionId: string;
  };
};

type InitPreviewAction = {
  type: 'initPreview';
  payload: {
    form: Form;
    elements: FormElement[];
  };
};

type SetErrorAction = {
  type: 'setError';
  payload: FormError;
};

type GetElementAction = {
  type: 'getElement';
};

type SetElementAction = {
  type: 'setElement';
  payload: {
    element: FormElement;
    shouldRefetch: boolean;
    answerVal?: string | string[];
  };
};

type GetReviewPageAction = {
  type: 'getReviewPage';
};

type SetReviewPageAction = {
  type: 'setReviewPage';
  payload: {
    elements: FormElement[];
    submission: FormSubmission;
  };
};

type SetEditElementAction = {
  type: 'setEditElement';
  payload: {
    element: FormElement;
    answerVal: string | string[];
  };
};

type CancelEditAction = {
  type: 'cancelEdit';
};

export type Action =
  | InitAction
  | InitPreviewAction
  | SetErrorAction
  | GetElementAction
  | SetElementAction
  | GetReviewPageAction
  | SetReviewPageAction
  | SetEditElementAction
  | CancelEditAction;

const handlers: HandlerType<RenderFormContextData, Action> = {
  init: (state, action: InitAction) => ({
    ...state,
    loading: false,
    form: action.payload.form,
    element: action.payload.element,
    groups: action.payload.form.formGroups,
    totalElements: action.payload.totalElements,
    submissionId: action.payload.submissionId,
    nextBehavior: NextBehavior.Linear,
  }),
  initPreview: (state, action: InitPreviewAction) => ({
    ...state,
    form: action.payload.form,
    elements: action.payload.elements,
    groups: action.payload.form.formGroups,
    loading: false,
    totalElements: action.payload.elements.length,
    nextBehavior: NextBehavior.Linear,
  }),
  setError: (state, action: SetErrorAction) => ({
    ...state,
    error: action.payload
  }),
  getElement: state => ({
    ...state,
    loading: true
  }),
  setElement: (state, action: SetElementAction) => ({
    ...state,
    loading: false,
    element: action.payload.element,
    shouldRefetch: action.payload.shouldRefetch,
    answerVal: action.payload.answerVal,
  }),
  getReviewPage: state => ({
    ...state,
    loading: true
  }),
  setReviewPage: (state, action: SetReviewPageAction) => ({
    ...state,
    loading: false,
    elements: action.payload.elements,
    submission: action.payload.submission,
    reviewPageElement: state.element,
    nextBehavior: NextBehavior.SkipToLast,
    shouldRefetch: false,
  }),
  setEditElement: (state, action: SetEditElementAction) => ({
    ...state,
    loading: false,
    element: action.payload.element,
    answerVal: action.payload.answerVal,
  }),
  cancelEdit: state => ({
    ...state,
    element: state.reviewPageElement
  }),
};

export const reducer: ReducerType<RenderFormContextData, Action> = (state, action) =>
  handlers[action.type]
    ? handlers[action.type](state, action)
    : state;

export const RenderFormContext = React.createContext<RenderFormContextState | null>(null);

export const useRenderFormContext = () => useContext(RenderFormContext);
